import { events } from '../api';
import clientLogger from '../logging/client.logger';

const HEIC_EXTENSIONS = ['heic', 'heif'];
const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const JPEG_TYPE = 'image/jpeg';
const JPEG_EXTENSION = 'jpg';
const JPEG_QUALITY = 0.9;
const NO_PROGRESS = 0;

const extensionOf = (name) => {
    const parts = String(name || '').split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

export const isHeicPhoto = (file) =>
    HEIC_EXTENSIONS.includes(extensionOf(file && file.name))
    || HEIC_TYPES.includes(String((file && file.type) || '').toLowerCase());

const jpegNameOf = (name) => {
    const dotIndex = String(name || '').lastIndexOf('.');
    const base = dotIndex > 0 ? name.slice(0, dotIndex) : String(name || 'foto');
    return `${base}.${JPEG_EXTENSION}`;
};

const conversionError = (file) => {
    const message = `Não foi possível converter a foto "${file.name}" para JPG e nada foi enviado. Envie a foto em formato JPG ou tire um print dela e anexe o print.`;
    return Object.assign(new Error(message), { info: message, response: { data: { message } } });
};

const decodeToJpeg = async (file) => {
    const { heicTo } = await import('heic-to');
    const blob = await heicTo({ blob: file, type: JPEG_TYPE, quality: JPEG_QUALITY });
    if (!blob || !blob.size) throw new Error('Conversão devolveu imagem vazia.');
    const converted = new File([blob], jpegNameOf(file.name), { type: JPEG_TYPE, lastModified: file.lastModified });
    return Object.assign(converted, file);
};

const reportFailure = (file, error) => clientLogger.logClientError({
    context: 'documents:heic-conversion',
    message: 'Conversão da foto HEIC para JPG falhou no navegador',
    payload: { fileSize: file.size, fileType: file.type, extension: extensionOf(file.name) },
    error,
});

const convertOne = (file) => decodeToJpeg(file).catch((error) => {
    reportFailure(file, error);
    throw conversionError(file);
});

const showConversion = (file, show) => events.emmit('onProgressDeterminate', {
    info: `Convertendo a foto: ${file.name}`,
    progress: NO_PROGRESS,
    show,
});

const prepare = async (file, showProgress) => {
    if (!isHeicPhoto(file)) return file;
    if (showProgress) showConversion(file, true);
    try {
        return await convertOne(file);
    } finally {
        if (showProgress) showConversion(file, false);
    }
};

export const convertHeicPhotos = async (files, { showProgress = false } = {}) => {
    const selected = Array.from(files);
    if (!selected.some(isHeicPhoto)) return selected;
    const prepared = [];
    for (const file of selected) prepared.push(await prepare(file, showProgress));
    return prepared;
};
